import { PaymentStatus, PaymentType } from '@dataTypes/billing.types'
import type { Provider } from '@dataTypes/provider.types'
import type { BillingInvoiceFilters } from '@services/api/billing/billing.api'
import type { LedgerQueryFilters } from '@services/api/billing/ledger.api'
import type { PaymentQueryFilters } from '@services/api/billing/payment.api'
import type {
  TransactionQueryFilters,
  TransactionType,
} from '@services/api/billing/transaction.api'

const ALL_INVOICE_STATUSES = Object.values(PaymentStatus)

type SelectedContext = {
  selectedPatientId?: string
  selectedClinicId?: string
}

export const buildContextFilters = ({
  selectedPatientId,
  selectedClinicId,
}: SelectedContext): Pick<
  BillingInvoiceFilters,
  'patient_id' | 'clinic_id'
> => {
  const contextFilters: Pick<
    BillingInvoiceFilters,
    'patient_id' | 'clinic_id'
  > = {}
  if (selectedPatientId) {
    contextFilters.patient_id = selectedPatientId
  }
  if (selectedClinicId) {
    contextFilters.clinic_id = selectedClinicId
  }
  return contextFilters
}

export const buildInvoiceFilters = ({
  search,
  tabFilters,
  sort,
  selectedPatientId,
  selectedClinicId,
}: {
  search: string
  tabFilters: Record<string, string[]>
  sort?: string
  selectedPatientId?: string
  selectedClinicId?: string
}): BillingInvoiceFilters => {
  const out: BillingInvoiceFilters = {
    ...buildContextFilters({ selectedPatientId, selectedClinicId }),
  }
  const trimmed = search.trim()
  if (trimmed) {
    out.search = trimmed
  }
  if (sort) {
    out.sort = sort
  }

  const statusSelection = tabFilters.status
  if (statusSelection?.length) {
    const isFullSelection =
      statusSelection.length === ALL_INVOICE_STATUSES.length &&
      ALL_INVOICE_STATUSES.every((status) => statusSelection.includes(status))
    if (!isFullSelection) {
      out.invoice_status =
        statusSelection.length === 1 ? statusSelection[0] : statusSelection
    }
  }

  return out
}

export const buildProviderNameToIdsMap = (
  providers: Provider[],
): Map<string, string[]> => {
  const map = new Map<string, string[]>()
  providers.forEach((provider) => {
    const name = `Dr. ${provider.first_name} ${provider.last_name}`
    const currentIds = map.get(name) ?? []
    map.set(name, [...currentIds, provider.provider_id])
  })
  return map
}

export const getSelectedProviderIds = (
  selectedProviderNames: string[] | undefined,
  providerNameToIdsMap: Map<string, string[]>,
): string[] => {
  if (!selectedProviderNames || selectedProviderNames.length === 0) {
    return []
  }
  const ids = selectedProviderNames.flatMap(
    (name) => providerNameToIdsMap.get(name) ?? [],
  )
  return Array.from(new Set(ids))
}

export const buildLedgerFilters = ({
  search,
  selectedProviderIds,
  sort,
  selectedPatientId,
  selectedClinicId,
}: {
  search: string
  selectedProviderIds: string[]
  sort?: string
  selectedPatientId?: string
  selectedClinicId?: string
}): LedgerQueryFilters => {
  const out: LedgerQueryFilters = {
    ...buildContextFilters({ selectedPatientId, selectedClinicId }),
  }
  const trimmed = search.trim()
  if (trimmed) {
    out.search = trimmed
  }
  if (sort) {
    out.sort = sort
  }
  if (selectedProviderIds.length === 1) {
    out.provider_id = selectedProviderIds[0]
  } else if (selectedProviderIds.length > 1) {
    out.provider_id = selectedProviderIds
  }
  return out
}

export const buildPaymentFilters = ({
  search,
  tabFilters,
  sort,
  selectedPatientId,
  selectedClinicId,
}: {
  search: string
  tabFilters: Record<string, string[]>
  sort?: string
  selectedPatientId?: string
  selectedClinicId?: string
}): PaymentQueryFilters => {
  const out: PaymentQueryFilters = {
    ...buildContextFilters({ selectedPatientId, selectedClinicId }),
  }
  const trimmed = search.trim()
  if (trimmed) {
    out.search = trimmed
  }
  if (sort) {
    out.sort = sort
  }
  const methods = (tabFilters.method ?? []).filter(
    (method): method is PaymentType =>
      method === PaymentType.CASH ||
      method === PaymentType.CARD ||
      method === PaymentType.ONLINE,
  )
  if (methods.length === 1) {
    out.payment_type = methods[0]
  }
  if (methods.length > 1) {
    out.payment_type = methods
  }
  return out
}

export const buildTransactionFilters = ({
  search,
  tabFilters,
  sort,
  selectedPatientId,
  selectedClinicId,
}: {
  search: string
  tabFilters: Record<string, string[]>
  sort?: string
  selectedPatientId?: string
  selectedClinicId?: string
}): TransactionQueryFilters => {
  const out: TransactionQueryFilters = {
    ...buildContextFilters({ selectedPatientId, selectedClinicId }),
  }
  const trimmed = search.trim()
  if (trimmed) {
    out.search = trimmed
  }
  if (sort) {
    out.sort = sort
  }
  const types = (tabFilters.transaction_type ?? []).filter(
    (type): type is TransactionType =>
      type === 'Billing' || type === 'Claim' || type === 'Procedure',
  )
  if (types.length === 1) {
    out.transaction_type = types[0]
  }
  if (types.length > 1) {
    out.transaction_type = types
  }
  return out
}
